import pick from "lodash/pick";
import { useTranslation } from "react-i18next";
import { FormEvent, useContext, useEffect, useState, useRef } from "react";
import type { MutableRefObject } from "react";

import {
  Box,
  Flex,
  Form,
  Text,
  FormContext,
  Devider,
  Button,
  AlertsContext,
  Alerts,
  FormErrorTypes,
  ModalContext,
  toast,
} from "dash-ui";

import {
  GetPropertyByIdDocument,
  GetPropertyByIdQuery,
  useSetGeneralPropertyDetailsMutation,
} from "gql/generated.graphql";
import { ChangesConfirmationModal } from "containers/properties/components/ChangesConfirmationModal";
import { compareGeneralInfoData } from "containers/properties/components/ChangesConfirmationModal/ChangesConfirmationModal.helpers";

import useValidation from "./hooks/useValidation";
import { FormFields, SALE_TEAMS_FIELDS } from "./constants/constants";
import {
  SaleTeams,
  NameInput,
  CityInput,
  PlaceSelect,
  StreetInput,
  StateSelect,
  CountySelect,
  ZipCodeInput,
  InventoryInfo,
  PriceRangeInput,
  MarketAreaSelect,
  OrganizationSelect,
  PrimaryOrganizationSelect,
} from "./components/";
import ContanctInfo from "./components/ContanctInfo/ContanctInfo";
import { ServerErrors, getIsMarketOptional } from "dash-common";
import { valeExistsOrIsZero } from "./helpers/isNumberOrZero";

const PropertyGeneralInfoForm = ({
  property,
  formRef,
  onLoadingChange,
  onIsEqualChange,
}: {
  property: GetPropertyByIdQuery["getPropertyById"];
  formRef?: MutableRefObject<{ submit: () => void } | null>;
  onLoadingChange?: (loading: boolean) => void;
  onIsEqualChange?: (isEqual: boolean) => void;
}) => {
  const { t } = useTranslation(["forms", "common", "pages"]);
  const { toggleAlert } = useContext(AlertsContext);
  const internalFormRef = useRef<HTMLFormElement>(null);
  const {
    formState,
    formOnSubmit,
    formOnError,
    formOnSuccess,
    setFormValues,
    errorHandlers,
  } = useContext(FormContext);

  const [isPlaceUpdate, setIsPlaceUpdate] = useState(false);

  const { toggleModal, setPayload } = useContext(ModalContext);
  const validateFieldByName = useValidation();
  const [setGeneralProperty, { loading }] =
    useSetGeneralPropertyDetailsMutation({
      refetchQueries: [
        {
          query: GetPropertyByIdDocument,
          variables: { propertyId: property._id },
        },
      ],
    });

  useEffect(() => {
    if (formRef) {
      formRef.current = {
        submit: () => internalFormRef.current?.requestSubmit()
      };
    }
  }, [formRef]);

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(loading);
    }
  }, [loading, onLoadingChange]);

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback

    setFormValues({
      name: property.name,
      primaryOrganization: property.primaryOrganization?._id,
      organizations: property.organizations?.map((org) => org._id),
      market: property?.marketArea?._id,
      website: property.website,
      county: property.location.address?.county,
      state: property.location.address?.state,
      city: property.location.address?.city,
      street: property.location.address?.street,
      zipCode: property.location.address?.zipCode,
      saleTeams: property.saleTeams?.length
        ? property.saleTeams.map((saleTeam) =>
            pick(saleTeam, SALE_TEAMS_FIELDS)
          )
        : [{}],
      defaultInventoryDetails: property.defaultInventoryDetails,
      mainPropertyMediaId: property.mainPropertyMediaId,
      prices: {
        min: property.priceRange?.min,
        max: property.priceRange?.max,
      },
      place: {
        place_id: property.location?.googlePlaceId,
        lat: property.location.geoLocation?.coordinates[1],
        lng: property.location.geoLocation?.coordinates[0],
      },
      averageBedroomsNumber: {
        min: valeExistsOrIsZero(property.averageBedroomsNumber?.min)
          ? parseFloat(`${property.averageBedroomsNumber?.min}`)
          : null,
        max: valeExistsOrIsZero(property.averageBedroomsNumber?.max)
          ? parseFloat(`${property.averageBedroomsNumber?.max}`)
          : null,
      },
      averageBathroomsNumber: {
        min: valeExistsOrIsZero(property.averageBathroomsNumber?.min)
          ? parseFloat(`${property.averageBathroomsNumber?.min}`)
          : null,
        max: valeExistsOrIsZero(property.averageBathroomsNumber?.max)
          ? parseFloat(`${property.averageBathroomsNumber?.max}`)
          : null,
      },
      contactEmail: property.contactEmail,
      contactPhone: property.contactPhone && {
        dialCode: property.contactPhone.dialCode,
        number: property.contactPhone.number,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property]);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    formOnSubmit();

    const { isLoading, errorSlug, ...formValues } = formState;
    if (
      !Object.entries(formValues)
        .map(([name, value]: any) => {
          if (name === FormFields.County || name === FormFields.State) {
            return validateFieldByName(name, value?.name);
          }
          if (name === FormFields.Market) {
            return validateFieldByName(
              name,
              value,
              getIsMarketOptional(formState.state)
            );
          }
          return validateFieldByName(name, value);
        })
        .every(Boolean)
    ) {
      return null;
    }

    const updateGeneralProperty = (noChangelog: boolean) => {
      const {
        name,
        primaryOrganization: primaryOrganizationId,
        organizations: organizationIds,
        market,
        state,
        website,
        saleTeams,
        mainPropertyMediaId,
        place,
        prices,
        defaultInventoryDetails,
        averageBedroomsNumber,
        averageBathroomsNumber,
        contactPhone,
        contactEmail,
        ...address
      } = formValues;

      setGeneralProperty({
        variables: {
          input: {
            propertyDetails: {
              propertyId: property._id,
              name,
              website,
              priceRange: prices,
              mainPropertyMediaId,
              defaultInventoryDetails,
              primaryOrganizationId,
              organizationIds,
              marketAreaId: market,
              averageBedroomsNumber: {
                min: valeExistsOrIsZero(averageBedroomsNumber?.min)
                  ? parseFloat(`${averageBedroomsNumber?.min}`)
                  : null,
                max: valeExistsOrIsZero(averageBedroomsNumber?.max)
                  ? parseFloat(`${averageBedroomsNumber?.max}`)
                  : null,
              },
              averageBathroomsNumber: {
                min: valeExistsOrIsZero(averageBathroomsNumber?.min)
                  ? parseFloat(`${averageBathroomsNumber?.min}`)
                  : null,
                max: valeExistsOrIsZero(averageBathroomsNumber?.max)
                  ? parseFloat(`${averageBathroomsNumber?.max}`)
                  : null,
              },
              location: {
                state,
                county: address.county as string,
                city: address.city,
                zipCode: address.zipCode,
                street: address.street,
                googlePlaceId: place.place_id,
                lat: place?.geometry?.location.lat() || place?.lat,
                lng: place?.geometry?.location.lng() || place?.lng,
              },
              saleTeams: saleTeams.reduce(
                (memo: any[], saleTeam: any) =>
                  saleTeam.saleTeamId
                    ? [...memo, pick(saleTeam, SALE_TEAMS_FIELDS)]
                    : memo,
                []
              ),
              contactPhone,
              contactEmail,
            },
            noChangelog,
          },
        },
        onCompleted: () => {
          formOnSuccess();
          toggleAlert(Alerts.ChangesSaved, true);
        },
        onError: (error) => {
          if (error.message === ServerErrors.PROPERTY_NAME_EXISTS) {
            return errorHandlers[FormFields.Name]({
              type: FormErrorTypes.Invalid,
              message: t(
                `common:serverErrors.${ServerErrors.PROPERTY_NAME_EXISTS}`
              ),
            });
          }

          // Show specific error messages for validation errors
          if (error.message.includes("Primary organization is required")) {
            toast.error(
              "Primary organization is required for properties with CMS status other than Proposed"
            );
          } else if (error.message.includes("Complete location information")) {
            toast.error(
              "Complete location information is required for properties with CMS status other than Proposed"
            );
          } else {
            // Try to translate the error, or show the raw message if translation doesn't exist
            const translatedMessage = t(
              `common:serverMessages.${error.message}`,
              { defaultValue: "" }
            );
            const errorMessage = translatedMessage || error.message;
            toast.error(errorMessage);
          }

          formOnError(error.message);
        },
      });
    };

    setPayload({ updateWithoutChangelog: updateGeneralProperty });
    toggleModal(true, "CreateChangeLogModal");
  };

  const isEqual = compareGeneralInfoData(property, formState);

  useEffect(() => {
    if (onIsEqualChange) {
      onIsEqualChange(isEqual);
    }
  }, [isEqual, onIsEqualChange]);

  return (
    <>
      <Form onSubmit={onSubmit} w="100%" ref={internalFormRef}>
        <ChangesConfirmationModal condition={isEqual} />
        <Box mb={4} mt={2}>
          <Text lg semibold>
            {t("common:labels.general")}
          </Text>
        </Box>
      <Form.Group w="100%" mt={4}>
        <NameInput />
      </Form.Group>
      <Form.Group w="100%" mt={4}>
        <Flex flex={1}>
          <PrimaryOrganizationSelect />
          <OrganizationSelect />
          <PriceRangeInput />
        </Flex>
      </Form.Group>
      <Form.Group w="100%" mt={4}>
        <ContanctInfo />
      </Form.Group>
      <InventoryInfo property={property} />
      <Devider mt={6} mx={-6} mb={4} />
      <SaleTeams />
      <Devider mt={6} mx={-6} />
      <Form.Group w="100%" mt={4}>
        <Text semibold lg>
          {t("common:labels.location")}
        </Text>
      </Form.Group>
      <Form.Group w="100%" mt={6}>
        {/* Shows only when clicked, to reduce unnecessary Google Maps API calls */}
        {isPlaceUpdate ? (
          <PlaceSelect defaultPlaceId={property.location?.googlePlaceId} />
        ) : (
          <Flex mb={3}>
            <Button onClick={() => setIsPlaceUpdate(true)}>
              Update Address & Coordinates via Map
            </Button>
          </Flex>
        )}
        <Flex flex={1}>
          <StreetInput />
          <CityInput />
          <ZipCodeInput />
        </Flex>
      </Form.Group>
      <Form.Group w="100%" mt={4}>
        <Flex flex={1}>
          <StateSelect />
          <CountySelect />
          <MarketAreaSelect />
        </Flex>
      </Form.Group>
      <Devider mt={6} mx={-6} />
      </Form>
    </>
  );
};

export default PropertyGeneralInfoForm;
